/* TextEditorRenderer.js */

"use strict";
const _ = require("lodash");

class TextEditorRenderer {
    constructor(model) {
        this._model = model;
        this._rootElement = document.createElement("text-editor-control");
        this._editorDiv = document.createElement("div");
        this._editorDiv.classList.add("text-editor");
        this._initializeEditor();
        this._errorDiv = document.createElement("div");
        this._errorDiv.classList.add("errors");
        this._rootElement.append(this._editorDiv);
        this._rootElement.append(this._errorDiv);
        this._onDocumentClick = this._onDocumentClick.bind(this);
        this._resizeObserver = new ResizeObserver((this._onResize = this._onResize.bind(this)));

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("LanguageMode", (this._onLanguageMode = this._onLanguageMode.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on(
            "ShowLineNumbers",
            (this._onShowLineNumbers = this._onShowLineNumbers.bind(this))
        );
        this._model.on(
            "ShowHiddenText",
            (this._onShowHiddenText = this._onShowHiddenText.bind(this))
        );
        this._model.on("TabSize", (this._onTabSize = this._onTabSize.bind(this)));
        this._model.on("Title", (this._onTitle = this._onTitle.bind(this)));
        this._model.on("Required", (this._onRequired = this._onRequired.bind(this)));
        this._model.on(
            "ValidationVerbiage",
            (this._onValidationVerbiage = this._onValidationVerbiage.bind(this))
        );
        this._model.on("AutoResize", (this._onAutoResize = this._onAutoResize.bind(this)));
        this._editor.addEventListener(
            "input",
            (this._onInputEvent = this._onInputEvent.bind(this))
        );
        this._editorDiv.addEventListener(
            "contextmenu",
            (this._showContextMenu = this._showContextMenu.bind(this))
        );
        this._rootElement.addEventListener(
            "mouseleave",
            (this._onMouseLeave = this._onMouseLeave.bind(this))
        );
    }
    _unregisterListeners() {
        this._resizeObserver.disconnect();
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("LanguageMode", this._onLanguageMode);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("ShowLineNumbers", this._onShowLineNumbers);
        this._model.removeListener("ShowHiddenText", this._onShowHiddenText);
        this._model.removeListener("TabSize", this._onTabSize);
        this._model.removeListener("Title", this._onTitle);
        this._model.removeListener("Required", this._onRequired);
        this._model.removeListener("ValidationVerbiage", this._onValidationVerbiage);
        this._model.removeListener("AutoResize", this._onAutoResize);
        this._editor.removeEventListener("input", this._onInputEvent);
        this._editorDiv.removeEventListener("contextmenu", this._showContextMenu);
        document.removeEventListener("click", this._onDocumentClick);
        this._rootElement.removeEventListener("mouseleave", this._onMouseLeave);
    }

    _initializeEditor() {
        const ace = require("ace-builds/src-min-noconflict/ace");

        let basePath = "/resources/ace-builds/src-min-noconflict";
        if (window.location.pathname !== "/" && window.location.pathname !== "/index.html") {
            basePath = window.location.pathname + basePath;
        }
        ace.config.set("basePath", basePath);
        this._editor = ace.edit(this._editorDiv, {
            minLines: this._model.minLineCount,
            maxLines: this._model.maxLineCount,
        });
        this._editor.setShowPrintMargin(false);
        this._editor.setShowFoldWidgets(true);
        this._editor.setFadeFoldWidgets(true);
        this._editor.setHighlightSelectedWord(true);
        this._editor.$blockScrolling = Infinity;
        this._editor.session.setFoldStyle("markbegin");
        this._editor.commands.addCommand({
            name: "unfind",
            bindKey: {
                win: "Ctrl-F",
                mac: "Command-F",
            },
            exec: () => {
                this._showSearch.apply(this);
            },
        });
        this._editor.commands.addCommand({
            name: "replace",
            bindKey: {
                win: "Ctrl-R",
                mac: "Command-R",
            },
            exec: () => {
                this._showReplace.apply(this);
            },
        });
        this._editor.commands.addCommand({
            name: "escape",
            bindKey: {
                win: "Esc",
                mac: "Esc",
            },
            exec: () => {
                this._contextMenu.hide();
            },
        });

        let Tooltip = ace.require("ace/tooltip").Tooltip;
        this._contextMenu = new Tooltip(this._rootElement);
        let contextMenuContent =
            '<div class="context-menu">' +
            '<span class="find-menuitem">Find</span>' +
            '<span class="replace-menuitem">Replace</span>' +
            '<span class="undo-menuitem">Undo</span>' +
            '<span class="redo-menuitem">Redo</span>' +
            '<span class="show-linenumbers-menuitem checkbox"><input type="checkbox" value="showLineNumbers"/>Show Line Numbers</span>' +
            '<span class="show-hidden-text-menuitem checkbox"><input type="checkbox" value="showHiddenText"/>Show Hidden Text</span>' +
            "</div>";
        this._contextMenu.setHtml(contextMenuContent);
        this._contextMenu.$element.classList.add("editor_contextmenu");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElement down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*, reject*/) => {
            let isInvalid = this._model.invalid;
            this._setLanguageMode(this._model.languageMode);
            this._setValue(this._model.value);
            this._setDisabled(this._model.disabled);
            this._setVisible(this._model.visible);
            this._setPlaceholder(this._model.placeholder);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setShowLineNumbers(this._model.showLineNumbers);
            this._setShowHiddenText(this._model.showHiddenText);
            this._setTitle(this._model.title);
            this._setRequired(this._model.required);
            this._setTabSize(this._model.tabSize);
            this._setAutoResize(this._model.autoResize);
            this._editor.gotoLine(1, 0);
            setTimeout(() => {
                this._setInvalid(isInvalid || this._isInvalid());
            }, 100);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElement;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /**
     * Clean up resources during shutdown
     * @returns {Promise}
     */
    shutdown() {
        let prom = new Promise((resolve /*, reject*/) => {
            this._unregisterListeners();
            this._editor.destroy();
            this._model = undefined;
            this._rootElement.remove();
            this._editor = undefined;
            this._rootElement = undefined;
            resolve();
        });
        return prom;
    }

    /**
     * Returns validation messages based on the validity of text value on this control
     * @returns {Array}
     */
    getViolations() {
        let validationMessages = this._isInvalid() ? [this.model.validationVerbiage.required] : [];
        return validationMessages;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when auto resize property is updated on model
     * @param autoResize
     * @private
     */
    _onAutoResize(autoResize) {
        this._setAutoResize(autoResize);
    }

    _onMouseLeave() {
        this._contextMenu.hide();
    }

    /**
     * Fired when root element of this control is resized
     * @private
     */
    _onResize() {
        this._autoResizeEditor();
    }

    _showContextMenu(e) {
        document.addEventListener("click", this._onDocumentClick);
        this._contextMenu.show(null, e.pageX - 4, e.pageY);
        e.preventDefault();
    }

    _onDocumentClick(e) {
        if (!this._contextMenu.$element.contains(e.target)) {
            document.removeEventListener("click", this._onDocumentClick);
        } else {
            let targetClassList = e.target.classList;
            if (targetClassList.contains("find-menuitem")) {
                this._showSearch();
            } else if (targetClassList.contains("replace-menuitem")) {
                this._showReplace();
            } else if (targetClassList.contains("undo-menuitem")) {
                this._undo();
            } else if (targetClassList.contains("redo-menuitem")) {
                this._redo();
            } else if (targetClassList.contains("show-linenumbers-menuitem")) {
                this.model.showLineNumbers = !this.model.showLineNumbers;
            } else if (targetClassList.contains("show-hidden-text-menuitem")) {
                this.model.showHiddenText = !this.model.showHiddenText;
            } else if (e.target.type === "checkbox") {
                if (e.target.parentElement.classList.contains("show-linenumbers-menuitem")) {
                    this.model.showLineNumbers = e.target.checked;
                } else {
                    this.model.showHiddenText = e.target.checked;
                }
            }
        }
        this._contextMenu.hide();
    }

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when the languageMode has been updated in the model
     * @param newMode {String}
     * @private
     */
    _onLanguageMode(newMode) {
        this._setLanguageMode(newMode);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    _onShowLineNumbers() {
        this._setShowLineNumbers(this.model.showLineNumbers);
    }

    _onShowHiddenText() {
        this._setShowHiddenText(this.model.showHiddenText);
    }

    /**
     * Fired when setTitle has been called on the model
     * @param newVal {String} - title to be set on the control
     * @private
     */
    _onTitle(newVal) {
        this._setTitle(newVal);
    }

    _onRequired(newRequiredVal) {
        this._setRequired(newRequiredVal);
    }

    _onTabSize(tabSize) {
        this._setTabSize(tabSize);
    }
    /**
     * Fired when valiation Messages verbiage is updated on the model
     * @param newValidationVerbiage
     * @private
     */
    _onValidationVerbiage(newValidationVerbiage) {
        this._setValidationVerbiage(newValidationVerbiage);
    }

    /**
     * Update the value in the model based on user input
     * @param event {Event}
     * @private
     */
    _onInputEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        let newVal = this._editor.getSession().getValue();
        this._model.setValue(newVal, true);
        this._setInvalid(this._isInvalid());
        this._model._triggerUserValue(newVal, oldVal, event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _setAutoResize(autoResize) {
        if (autoResize) {
            this._rootElement.classList.add("full-height");
            this._resizeObserver.observe(this._rootElement);
            this._autoResizeEditor();
        } else {
            this._resizeObserver.disconnect();
            this._rootElement.classList.remove("full-height");
        }
    }

    _autoResizeEditor() {
        this._setEditorHeight(this._rootElement.clientHeight);
    }
    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal) {
            this._rootElement.classList.add("disabled");
        } else {
            this._rootElement.classList.remove("disabled");
        }
        if (this._editor) {
            this._editor.setReadOnly(newVal);
        }
        this._model.invalid = newVal === true ? false : this._isInvalid();
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._rootElement.classList.add("invalid");
        } else {
            this._rootElement.classList.remove("invalid");
        }
        this._model._invalid = newVal === true;
        this._setValidationVerbiage();
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(/*newVal*/) {
        // TODO: implement placeholder which is not supported by ace editor
        // let newPlaceholder = _.isEmpty(newVal) ? "" : newVal;
        // this._editor.setOptions({placeholder: newPlaceholder});
    }

    /**
     * Toggle the display text
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        if (newVal !== this._editor.getSession().getValue()) {
            if (newVal) {
                this._editor.getSession().setValue(newVal);
            } else {
                this._editor.getSession().setValue("");
            }
            this._setInvalid(this._isInvalid());
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElement.classList.remove("hidden");
        } else {
            this._rootElement.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElement.classList.add("dark");
        } else {
            this._rootElement.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (newVal !== oldVal && !_.isEmpty(newVal)) {
            let newThemePath = newVal.startsWith("ace/") ? newVal : "ace/theme/" + newVal;
            this._editor.setTheme(newThemePath);
            // this._editor.session.setValue(this.model.value);
        }
    }

    /**
     * Set the title/tooltip
     * @param newVal {String}
     * @private
     */
    _setTitle(newVal) {
        if (!_.isEmpty(newVal)) {
            this._rootElement.setAttribute("title", newVal);
        } else {
            this._rootElement.setAttribute("title", "");
        }
    }

    _setRequired(newRequiredVal) {
        if (newRequiredVal === true) {
            this._editorDiv.classList.add("required");
        } else {
            this._editorDiv.classList.remove("required");
        }
        this._setInvalid(this._isInvalid());
    }

    _setTabSize(tabSize) {
        this._editor.session.setTabSize(tabSize);
    }

    /**
     * Update validation messages verbiage displayed on UI in case of validation errors
     * @param newValidationVerbiage
     * @private
     */
    _setValidationVerbiage() {
        this._errorDiv.innerText = _.join(this.getViolations(), ", ");
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._editor.focus();
        } else {
            this._editor.blur();
        }
    }

    _setLanguageMode(newMode) {
        if (!_.isEmpty(newMode)) {
            let newModePath = newMode.startsWith("ace/") ? newMode : "ace/mode/" + newMode;
            this._editor.session.setMode(newModePath);
        }
    }

    _isInvalid() {
        return !this._model.disabled && this._model.required && _.isEmpty(this._model.value);
    }

    _showSearch() {
        if (this._editor.searchBox) {
            this._editor.searchBox.show();
        } else {
            ace.config.loadModule("ace/ext/searchbox", (e) => {
                e.Search(this._editor);
            });
        }
    }

    _showReplace() {
        if (this._editor.searchBox) {
            this._editor.searchBox.show(null, true);
        } else {
            ace.config.loadModule("ace/ext/searchbox", (e) => {
                e.Search(this._editor);
            });
            setTimeout(() => {
                if (this._editor.searchBox) {
                    this._editor.searchBox.show(null, true);
                }
            }, 100);
        }
    }

    _setShowLineNumbers(showLineNumbers) {
        this._editor.renderer.setShowGutter(showLineNumbers);
        let showGutterCheckbox = this._contextMenu.$element.getElementsByClassName(
            "show-linenumbers-menuitem"
        )[0].children[0];
        if (showGutterCheckbox.checked !== showLineNumbers) {
            showGutterCheckbox.checked = showLineNumbers;
        }
    }

    _setShowHiddenText(showHiddenText) {
        this._editor.setShowInvisibles(showHiddenText);
        let showHiddenTextCheckbox = this._contextMenu.$element.getElementsByClassName(
            "show-hidden-text-menuitem"
        )[0].children[0];
        if (showHiddenTextCheckbox.checked !== showHiddenText) {
            showHiddenTextCheckbox.checked = showHiddenText;
        }
    }

    _setEditorHeight(newHeight) {
        if (!isNaN(newHeight) && this._editor) {
            let minHeight = 120;
            let editorHeight = newHeight > minHeight ? newHeight : minHeight;
            this._editor.setOptions({minLines: null, maxLines: null});
            this._editorDiv.style.height = editorHeight + "px";
            this._editor.resize();
        }
    }

    _undo() {
        this._editor.undo();
    }

    _redo() {
        this._editor.redo();
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = TextEditorRenderer;
